import { get, post, apiFetch } from '../client';

import type {
  FavouriteSetResponse,
  PlaylistTrack,
  TrackFileType,
} from '../types';
import type { UserPlaylist, FavouriteType } from '@/lib/contexts/favouriting';

export const getUserPlaylists = async (): Promise<UserPlaylist[]> => {
  return get<UserPlaylist[]>('/radios?type=customer', { auth: 'required' });
};

export const addTrackToPlaylist = async (
  playlistId: number,
  trackId: number,
) => {
  await post<void>(
    `/playlists/${playlistId}/add/song`,
    { songs: [trackId] },
    { auth: 'required' },
  );
};

export const removeTrackFromPlaylist = async (
  playlistId: number,
  trackId: number,
) => {
  await post<void>(
    `/playlists/${playlistId}/remove/song`,
    { songs: trackId },
    { auth: 'required' },
  );
};

export const createUserPlaylist = async (name: string) => {
  const res = await post<{ playlistId: number }>(
    '/playlists/edit',
    {
      title: name,
      type: 'customer',
      description: '',
      enabled: true,
    },
    { auth: 'required' },
  );

  return { id: res.playlistId, title: name };
};

export const renameUserPlaylist = async (id: number, newName: string) => {
  await post<void>(
    `/playlists/${id}/rename`,
    { title: newName },
    { auth: 'required' },
  );
};

export const deleteUserPlaylist = async (id: number) => {
  await apiFetch<void>(`/playlists/${id}/delete`, {
    method: 'DELETE',
    auth: 'required',
  });
};

export const fetchAllSavedContent = async () => {
  return get<unknown>('/saved-content', { auth: 'required' });
};

export type RecentlyListenedResponse = {
  count: number;
  data: PlaylistTrack[];
  totalPages: number;
};

type FavouriteCollectionResponse<T = Record<string, unknown>> = {
  data: {
    items: T[];
  };
};

export const fetchRecentlyListened = async (
  lang: string,
  size: number,
  page: number,
): Promise<RecentlyListenedResponse> => {
  // Temporarily disabled because the endpoint currently returns 404.
  // return get<RecentlyListenedResponse>(
  //   `/clients/historique?lang=${lang}&limit=${size}&page=${page}`,
  //   { auth: 'required' },
  // );
  void lang;
  void size;
  void page;

  return { count: 0, data: [], totalPages: 0 };
};

export const fetchSavedArticles = async (lang: string) => {
  const res = await get<FavouriteCollectionResponse>(
    `/customer/favourite_news?lang=${lang}`,
    { auth: 'required' },
  );
  return res.data.items;
};

export const fetchSavedVideos = async (lang: string) => {
  const res = await get<FavouriteCollectionResponse>(
    `/customer/favourite_videos?lang=${lang}`,
    { auth: 'required' },
  );
  return res.data.items;
};

export const fetchUserCreatedPlaylists = async (lang: string) => {
  return get<UserPlaylist[]>(`/clients/playlists?lang=${lang}`, {
    auth: 'required',
  });
};

export const fetchSavedPlaylists = async (lang: string) => {
  const res = await get<FavouriteCollectionResponse>(
    `/customer/favourite_playlists?lang=${lang}`,
    { auth: 'required' },
  );
  return res.data.items;
};

export const fetchSavedTracks = async (lang: string) => {
  const res = await get<FavouriteCollectionResponse>(
    `/customer/favourite_tracks?lang=${lang}`,
    { auth: 'required' },
  );
  return res.data.items;
};

export const fetchSavedAlbums = async (lang: string) => {
  const res = await get<FavouriteCollectionResponse>(
    `/customer/favourite_albums?lang=${lang}`,
    { auth: 'required' },
  );
  return res.data.items;
};

export const fetchSavedCollections = async (lang: string) => {
  const res = await get<FavouriteCollectionResponse>(
    `/customer/favourite_partners?lang=${lang}`,
    { auth: 'required' },
  );
  return res.data.items;
};

export type LibraryFavouritesResponse = Record<FavouriteType, number[]>;

export const fetchAllFavouriteIds =
  async (): Promise<LibraryFavouritesResponse> => {
    const data = await get<{
      tracks?: number[];
      albums?: number[];
      playlists?: number[];
      videos?: number[];
      articles?: number[];
      partners?: number[];
      podcasts?: number[];
      images?: number[];
    }>('/b2b-sites/favourites', { auth: 'required' });

    return {
      track: data.tracks ?? [],
      album: data.albums ?? [],
      playlist: data.playlists ?? [],
      video: data.videos ?? [],
      article: data.articles ?? [],
      collection: data.partners ?? [],
      podcast: data.podcasts ?? [],
      image: data.images ?? [],
    } as LibraryFavouritesResponse;
  };

export const reorderPlaylistTracks = async (
  playlistId: string | number,
  songsOrder: string[],
) => {
  await post<void>(
    '/playlists/order',
    { destructive: true, id: playlistId, songs: songsOrder },
    { auth: 'required' },
  );
};

export const updateUserStreamQualityPreference = async (
  format: TrackFileType,
) => {
  await post<void>(`/stream_quality/${format}?platform=web`, undefined, {
    auth: 'required',
  });
};

const favouriteEndpointMap: Record<FavouriteType, string> = {
  track: 'tracks',
  album: 'albums',
  playlist: 'playlists',
  video: 'videos',
  article: 'news',
  collection: 'partners',
  podcast: 'podcasts',
  image: 'images',
  audio: 'audio',
};

/**
 * Set/unset a favourite.
 * - Uses auth required
 * - Uses backend convention: /{typePath}/{id}/favourite?lang={lang}
 */
export const setFavourite = async (args: {
  type: FavouriteType;
  id: number;
  lang: string;
  enabled: boolean;
}): Promise<FavouriteSetResponse> => {
  const { type, id, lang, enabled } = args;

  const typePath = favouriteEndpointMap[type];
  if (!typePath) {
    throw new Error(`Unsupported favourite type: ${type}`);
  }

  const url = `/${typePath}/${id}/favourite?lang=${encodeURIComponent(lang)}`;

  return apiFetch<FavouriteSetResponse>(url, {
    method: enabled ? 'POST' : 'DELETE',
    auth: 'required',
  });
};
